import type { PageSection } from '@crowdex/plugin-contracts';
import { Button } from '@crowdex/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { Check, ChevronDown, Plus } from 'lucide-react';
import type { PluginPanel } from '@/features/plugins/plugin-workbench';
import { type AppSection, useAppSectionItems } from './app-panel-sidebar';

/**
 * A docked extension's sections, in its tab's bar (the main sidebar stays the assistants'): the
 * live sections of what its view shows (`page.sections.set`: a design's pages, a deck's slides)
 * and the sections of its app (`page.sidebars`), each listing its items and "New".
 */
export function SectionsPicker({
  panel,
  sections,
  liveSections,
  section,
  item,
  onSelect,
  onPickLive,
}: {
  panel: PluginPanel;
  sections: readonly AppSection[];
  liveSections: readonly PageSection[];
  /** The section the shown item is of. */
  section: AppSection | null;
  item: string | null;
  onSelect(item: string | null, section: string): void;
  onPickLive(section: string, item: string): void;
}) {
  if (sections.length === 0 && liveSections.length === 0) return null;
  const live = liveSections[0];
  const liveSelected = live?.items.find((entry) => entry.id === live.selected);
  const label =
    liveSelected?.title ??
    (item === null ? section?.title : undefined) ??
    section?.title ??
    'Sections';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label={`${panel.title} sections`}
          className="min-w-0 max-w-48 shrink"
          data-sections-picker
          size="sm"
          type="button"
          variant="ghost"
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="size-3.5 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-96 min-w-56 overflow-y-auto">
        {liveSections.map((entry, index) => (
          <div key={entry.id}>
            {index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel>{entry.title}</DropdownMenuLabel>
            {entry.items.map((choice) => (
              <DropdownMenuItem key={choice.id} onSelect={() => onPickLive(entry.id, choice.id)}>
                <Check className={choice.id === entry.selected ? 'size-4' : 'size-4 opacity-0'} />
                <span className="truncate">{choice.title}</span>
              </DropdownMenuItem>
            ))}
          </div>
        ))}
        {liveSections.length > 0 && sections.length > 0 && <DropdownMenuSeparator />}
        {sections.map((entry) => (
          <DropdownMenuSub key={entry.id}>
            <DropdownMenuSubTrigger>{entry.title}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-96 min-w-56 overflow-y-auto">
              <SectionChoices
                current={section?.id === entry.id ? item : undefined}
                onSelect={(id) => onSelect(id, entry.id)}
                panel={panel}
                section={entry}
              />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A section's items, read from its route when its menu opens, and "New". */
function SectionChoices({
  panel,
  section,
  current,
  onSelect,
}: {
  panel: PluginPanel;
  section: AppSection;
  /** The item shown, when it is of this section. */
  current: string | null | undefined;
  onSelect(item: string | null): void;
}) {
  const listed = useAppSectionItems(panel, section);
  return (
    <>
      <DropdownMenuItem onSelect={() => onSelect(null)}>
        <Plus className="size-4" />
        New
      </DropdownMenuItem>
      {listed.items.length > 0 && <DropdownMenuSeparator />}
      {listed.items.map((entry) => (
        <DropdownMenuItem key={entry.id} onSelect={() => onSelect(entry.id)}>
          <Check className={entry.id === current ? 'size-4' : 'size-4 opacity-0'} />
          <span className="truncate">{entry.title}</span>
        </DropdownMenuItem>
      ))}
      {listed.error !== null && <DropdownMenuLabel>{listed.error}</DropdownMenuLabel>}
    </>
  );
}
